import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MicrophoneErrorMessages } from '../../../util/constants';

@Injectable({
  providedIn: 'root'
})
export class AudioStreamService {
  private streamSubject = new BehaviorSubject<MediaStream | null>(null);
  stream$ = this.streamSubject.asObservable();

  /**
   * Always requests the browser's default microphone. No deviceId
   * constraint — getUserMedia resolves whichever mic the OS/browser
   * currently treats as default.
   */
  async init(): Promise<MediaStream> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false
      });
      this.streamSubject.next(stream);
      return stream;
    } catch (error) {
      console.error('[AudioStream] getUserMedia failed', error);
      throw new Error(this.getUserMediaErrorMessage(error));
    }
  }

  stop(): void {
    const stream = this.streamSubject.value;
    if (!stream) return;
    stream.getTracks().forEach(t => t.stop());
    this.streamSubject.next(null);
    console.log('[AudioStream] stream stopped');
  }

  async restart(): Promise<MediaStream> {
    this.stop();
    return this.init();
  }

  get current(): MediaStream | null {
    return this.streamSubject.value;
  }

  hasStream(): boolean {
    return !!this.streamSubject.value;
  }

  getUserMediaErrorMessage(error: unknown): string {
    if (error instanceof DOMException) {
      return MicrophoneErrorMessages[
        error.name as keyof typeof MicrophoneErrorMessages
      ] ?? MicrophoneErrorMessages.UnknownError;
    }
    return MicrophoneErrorMessages.UnknownError;
  }
}